/**
 * PATH: packages/email-hub/src/feature/components/SendHoldBanner.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-SENDHOLDBANNER-001
 * PURPOSE: The "undo send" grace-window banner shown while a send is queued:
 *          a live countdown with two escape hatches: "Send now" (skip the wait)
 *          and "Undo" (cancel, draft untouched).
 * WHY:     Operators asked for a Gmail-style grace period AND a way to bypass it
 *          when they're sure; waiting out the full window when you just want it
 *          gone is annoying. Kept as a tiny presentational component so the state
 *          machine stays in sendHold.ts/useUndoSend and ComposePage stays lean.
 * FLOW:
 *   ┌──────────────────────┐   ┌───────────────────────────┐
 *   │ secondsLeft (ticks)  │ → │ "Sending in Ns…" + buttons │
 *   └──────────────────────┘   └───────────────────────────┘
 *        onSendNow ─▶ fire immediately ; onUndo ─▶ cancel
 * DEPENDENCIES:
 *   - lucide-react: Loader2 (spinner), Send (send-now affordance)
 * RELATED:
 *   - compose/useUndoSend.ts: supplies { secondsLeft, cancel, sendNow }
 *   - pages/ComposePage.tsx: renders this while undoSend.pending
 */

import { Loader2, Send } from 'lucide-react'

export interface SendHoldBannerProps {
  /** Whole seconds remaining before the queued send fires. */
  secondsLeft: number
  /** Fire the queued send immediately, skipping the rest of the window. */
  onSendNow: () => void
  /** Cancel the queued send; the draft is left exactly as it was. */
  onUndo: () => void
}

/**
 * Presentational undo-send banner. Pure render of `secondsLeft` plus the two
 * action buttons; all timing lives in the send-hold controller.
 */
export function SendHoldBanner({ secondsLeft, onSendNow, onUndo }: SendHoldBannerProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="mx-4 mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-lg flex items-center gap-2"
    >
      <Loader2 className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 animate-spin" />
      <span className="text-sm text-amber-800 dark:text-amber-200 flex-1 min-w-0">
        Sending in {secondsLeft}s…
      </span>
      <button
        type="button"
        onClick={onSendNow}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-800/30 transition-colors"
        aria-label="Send now, skip the delay"
        title="Send now (skip the delay)"
      >
        <Send className="h-3.5 w-3.5" />
        Send now
      </button>
      <button
        type="button"
        onClick={onUndo}
        className="shrink-0 px-3 py-1.5 text-sm font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors"
        aria-label="Undo send"
      >
        Undo
      </button>
    </div>
  )
}

export default SendHoldBanner
